import React, { useEffect, useRef } from 'react';

/**
 * Minimal accessible dialog: backdrop click and Escape close it, focus moves
 * into the dialog on open and returns to the opener on close.
 */
export default function Modal({
  label,
  onClose,
  children
}: {
  /** Accessible name of the dialog. */
  label: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  // Latest onClose without re-running the mount effect (which would steal focus
  // back to the dialog on every parent render).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      opener?.focus?.();
    };
  }, []);

  return (
    <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} ref={dialogRef}>
        <div className="modal-head">
          <button type="button" className="btn modal-close" aria-label="Close" onClick={onClose}>
            Close
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
